import { Button } from '@/components/Button';
import { LineSeparator } from '@/components/LineSeparator';
import { Text } from '@/components/Text';
import { CreateEventCover } from '@/partials/dashboard/Events/CreateEvent/CreateEventCover';
import { CreateEventInformations } from '@/partials/dashboard/Events/CreateEvent/CreateEventInformations';
import { useEventStore } from '@/stores/useEventStore';
import { CreateEventForm } from '@/types/event';
import { handleError } from '@/utils/handleError';
import { Form, Formik } from 'formik';
import { motion } from 'framer-motion';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { FaArrowLeft } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import * as yup from 'yup';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
      duration: 0.3,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.3 },
  },
};

export const CreateEvent = () => {
  const navigate = useNavigate();
  const { createEvent } = useEventStore();
  const { t, i18n } = useTranslation();

  const validationSchema = yup.object().shape({
    name: yup.string().required(t('common:validation.name.required')),
    description: yup
      .string()
      .required(t('common:validation.description.required')),
    location: yup.string().required(t('common:validation.location.required')),
    date: yup.string().required(t('common:validation.date.required')),
    end_date: yup
      .string()
      .required(t('common:validation.end_date.required'))
      .test(
        'is-after-start-date',
        t('common:validation.end_date.after_start_date'),
        function (value) {
          const { date } = this.parent;
          if (!date || !value) return true; // Skip validation if either date is missing
          return new Date(value) > new Date(date);
        },
      ),
    email: yup.string().email(t('common:validation.email.format')),
    phone: yup.string(),
    type: yup.string().oneOf(['public', 'private']).required(),
    status: yup
      .string()
      .oneOf(['future', 'ongoing', 'past'])
      .required(t('common:validation.required')),
    category: yup.string(),
    organizer: yup.string(),
    cover: yup.string().nullable(),
    maximum_participants: yup
      .number()
      .min(1, t('common:validation.maximum_participants.min'))
      .required(t('common:validation.required')),
    show_capacity: yup.boolean().required(t('common:validation.required')),
  });

  const initialValues: CreateEventForm = {
    name: '',
    description: '',
    location: '',
    date: '',
    end_date: '',
    maximum_participants: 0,
    show_capacity: false,
    email: '',
    phone: '',
    type: 'public',
    status: 'future',
    category: '',
    organizer: '',
    cover: null,
  };

  const handleCreateEvent = async (values: CreateEventForm) => {
    try {
      const { data, message } = await createEvent(values);
      if (data) {
        navigate(`/${i18n.language}/dashboard/events/events-management`);
        toast.success(message);
      }
    } catch (error) {
      handleError(error);
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  return (
    <div className="h-full space-y-6 overflow-y-auto">
      {/* Header */}
      <motion.div
        className="flex flex-col gap-4 p-4"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        {/* Header avec bouton retour */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100"
          >
            <FaArrowLeft className="size-4" />
            {t('common:back')}
          </button>
          <div className="h-6 w-px bg-gray-300" />
          <h1 className="text-lg font-semibold text-secondary">
            {t('createEvent:title')}
          </h1>
        </div>
      </motion.div>

      {/* Contenu */}
      <motion.div
        className="px-4 pb-4"
        variants={containerVariants}
        initial="hidden"
        animate="visible"
      >
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleCreateEvent}
        >
          {({
            isSubmitting,
            errors,
            touched,
            handleSubmit,
            values,
            setFieldValue,
            handleChange,
          }) => (
            <Form onSubmit={handleSubmit} className="relative">
              <motion.div variants={itemVariants}>
                <CreateEventCover
                  setFieldValue={setFieldValue}
                  values={values}
                />
              </motion.div>

              <motion.div variants={itemVariants}>
                <LineSeparator className="my-6" />
              </motion.div>

              <motion.div variants={itemVariants}>
                <CreateEventInformations
                  errors={errors}
                  touched={touched}
                  values={values}
                  handleChange={handleChange}
                />
              </motion.div>

              <motion.div variants={itemVariants}>
                <LineSeparator className="my-6" />
              </motion.div>

              <motion.div variants={itemVariants}>
                <div className="flex flex-col space-y-4">
                  <Button
                    type="submit"
                    className="w-full lg:w-fit"
                    disabled={isSubmitting}
                  >
                    {t('createEvent:submit')}
                  </Button>
                  <Text className="text-sm font-light text-gray-500 lg:mt-2">
                    {t('createEvent:note')}
                  </Text>
                </div>
              </motion.div>
            </Form>
          )}
        </Formik>
      </motion.div>
    </div>
  );
};
